import type { ResumeData } from "@stylingcv/schema";
import { defaultResumeData } from "@stylingcv/schema";
import { useEffect, useState } from "react";

import { validateGuestResume } from "../services/resume/guest";
import { useAuthStore } from "../stores/auth";
import { useResumeStore } from "../stores/resume";

const LOCAL_STORAGE_KEY = "guest-resume";

// Ensure guest resume is properly structured
const sanitizeResumeData = (data: Partial<ResumeData> | undefined) => {
  if (!data) return defaultResumeData;

  // CRITICAL: Preserve AI-generated locale
  const originalLocale = data.metadata?.locale;

  const sanitized = {
    ...defaultResumeData,
    ...data,
    // Ensure sections and metadata are always defined
    sections: {
      ...defaultResumeData.sections,
      ...data.sections,
    },
    metadata: {
      ...defaultResumeData.metadata,
      ...data.metadata,
    },
    basics: {
      ...defaultResumeData.basics,
      ...data.basics,
    },
  };

  // FORCE preserve non-English locales after merge
  if (originalLocale && originalLocale !== "en-US") {
    sanitized.metadata.locale = originalLocale;
    console.log("✅ Guest sanitization FORCED locale preservation:", originalLocale);
  }

  return sanitized;
};

export const useGuestResume = () => {
  const isLoggedIn = useAuthStore((state) => !!state.user);
  const resumeData = useResumeStore((state) => state.resume.data);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load guest resume from local storage on first render
  useEffect(() => {
    if (isLoggedIn || isLoaded) return;

    const storedData = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (storedData) {
      try {
        JSON.parse(storedData);
        // Here we would ideally set the resume data in the store
        // But we don't want to overwrite the current resume if it exists
        // The actual implementation depends on the resume store structure
        setIsLoaded(true);
      } catch {
        // Silent fail - we'll use default data
        setIsLoaded(true);
      }
    }
  }, [isLoggedIn, isLoaded]);

  // Save guest resume to local storage when data changes
  const saveToLocalStorage = async () => {
    if (isLoggedIn) return false; // Don't save to local storage if logged in

    try {
      console.log("💾 Guest saveToLocalStorage - original locale:", resumeData?.metadata?.locale);
      const sanitizedData = sanitizeResumeData(resumeData);
      console.log(
        "💾 Guest saveToLocalStorage - sanitized locale:",
        sanitizedData?.metadata?.locale,
      );

      // Validate with the server first
      try {
        await validateGuestResume(sanitizedData);
      } catch {
        // Log error without exposing the error variable directly
        console.error("Failed to validate guest resume");
        return false;
      }

      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(sanitizedData));
      return true;
    } catch {
      return false;
    }
  };

  // Download resume as JSON
  const downloadAsJson = async () => {
    try {
      const sanitizedData = sanitizeResumeData(resumeData);

      // Validate with the server first
      try {
        await validateGuestResume(sanitizedData);
      } catch {
        // Log error without exposing the error variable directly
        console.error("Failed to validate guest resume");
        return false;
      }

      const dataStr = JSON.stringify(sanitizedData, null, 2);
      const dataUri = `data:application/json;charset=utf-8,${encodeURIComponent(dataStr)}`;

      const exportName = `resume-${new Date().toISOString().slice(0, 10)}.json`;

      const linkElement = document.createElement("a");
      linkElement.setAttribute("href", dataUri);
      linkElement.setAttribute("download", exportName);
      linkElement.click();

      return true;
    } catch {
      return false;
    }
  };

  return {
    saveToLocalStorage,
    downloadAsJson,
  };
};
